import { useEffect, useRef, useState } from "react";
import api, { num, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Plus, Trash2, Pencil, ClipboardList, FileText, ImageIcon, X, Upload, Loader2, Paperclip,
} from "lucide-react";
import { toast } from "sonner";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const imageUrl = (path) =>
  `${API_BASE}/stockgroup/rows/image/${path}?auth=${encodeURIComponent(localStorage.getItem("cf_token") || "")}`;
const isPdf = (name = "") => name.toLowerCase().endsWith(".pdf");
const parseNum = (v) => parseFloat(String(v).replace(/\./g, "").replace(",", ".")) || 0;
const emptyForm = () => ({
  product: "", store: "Falster", units: "", pallets: "", mix: "nej", deadline: "", comment: "",
  image_path: "", image_filename: "", content_type: "",
});

export function StockgroupOverview() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [storeFilter, setStoreFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  const [localPreview, setLocalPreview] = useState("");
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/stockgroup/rows"); setRows(r.data); }
    catch { toast.error("Kunne ikke hente oversigten"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(emptyForm()); setLocalPreview(""); setDialogOpen(true); };
  const openEdit = (d) => {
    setEditing(d);
    setLocalPreview("");
    setForm({
      product: d.product, store: d.store || "Falster", units: d.units ? String(d.units) : "",
      pallets: d.pallets || "", mix: d.mix ? "ja" : "nej", deadline: d.deadline || "", comment: d.comment || "",
      image_path: d.image_path || "", image_filename: d.image_filename || "", content_type: d.content_type || "",
    });
    setDialogOpen(true);
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) { toast.error("Filen er for stor (max 15 MB)"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/stockgroup/rows/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image_path: r.data.image_path, image_filename: r.data.image_filename, content_type: r.data.content_type || "" }));
      setLocalPreview(URL.createObjectURL(file));
      toast.success("Fil vedhæftet");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload fejlede");
    } finally { setUploading(false); }
  };
  const removeAttachment = () => { setLocalPreview(""); setForm((f) => ({ ...f, image_path: "", image_filename: "", content_type: "" })); };

  const save = async () => {
    if (!form.product.trim()) { toast.error("Skriv et produkt"); return; }
    const payload = { ...form, units: parseNum(form.units), mix: form.mix === "ja" };
    setSaving(true);
    try {
      if (editing) await api.put(`/stockgroup/rows/${editing.id}`, payload);
      else await api.post("/stockgroup/rows", payload);
      setDialogOpen(false);
      toast.success(editing ? "Vare opdateret" : "Vare tilføjet");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Kunne ikke gemme");
    } finally { setSaving(false); }
  };

  const remove = async (d) => {
    if (!window.confirm("Slet denne vare?")) return;
    try { await api.delete(`/stockgroup/rows/${d.id}`); toast.success("Vare slettet"); load(); }
    catch { toast.error("Kunne ikke slette"); }
  };

  const filtered = rows.filter((d) => storeFilter === "all" || d.store === storeFilter || d.store === "Begge");
  const totalUnits = filtered.reduce((s, d) => s + (d.units || 0), 0);

  return (
    <div className="space-y-5" data-testid="stockgroup-overview">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <h4 className="font-heading text-xl font-light text-slate-900 flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-emerald-600" /> Aftræksoversigt
          </h4>
          <p className="text-sm text-slate-500">{filtered.length} varer · {num(totalUnits)} enheder i alt</p>
        </div>
        <Button onClick={openNew} className="rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 gap-2 self-start sm:self-auto" data-testid="sg-row-new-btn">
          <Plus className="w-4 h-4" /> Ny vare
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {[["all", "Alle butikker"], ["Falster", "Falster"], ["Ringsted", "Ringsted"]].map(([id, label]) => (
          <button key={id} onClick={() => setStoreFilter(id)} data-testid={`sg-store-filter-${id}`}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${storeFilter === id ? "bg-emerald-600 text-white" : "bg-[#ffffff] border border-[#e2e8f0] text-slate-500 hover:text-slate-900"}`}>
            {label}
          </button>
        ))}
      </div>

      <div className="rounded-2xl bg-[#ffffff] border border-[#e2e8f0] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="sg-table">
            <thead>
              <tr className="bg-emerald-600 text-white text-left">
                <th className="px-3 py-2.5 font-semibold">Produkt</th>
                <th className="px-3 py-2.5 font-semibold">Butik</th>
                <th className="px-3 py-2.5 font-semibold text-right">Antal enheder</th>
                <th className="px-3 py-2.5 font-semibold">Antal paller</th>
                <th className="px-3 py-2.5 font-semibold">Mix</th>
                <th className="px-3 py-2.5 font-semibold">Aftræk inden</th>
                <th className="px-3 py-2.5 font-semibold">Kommentarer</th>
                <th className="px-3 py-2.5 font-semibold">Bilag</th>
                <th className="px-3 py-2.5" />
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={9} className="px-3 py-6 text-center text-slate-500" data-testid="sg-loading">Henter oversigt…</td></tr>
              )}
              {!loading && filtered.map((d) => (
                <tr key={d.id} className="border-t border-[#eef2f6] hover:bg-[#f8fafc]" data-testid={`sg-row-${d.id}`}>
                  <td className="px-3 py-2.5 font-medium text-slate-800">{d.product}</td>
                  <td className="px-3 py-2.5">
                    {d.store && <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-sky-500/15 text-sky-600 border border-sky-500/30">{d.store}</span>}
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-slate-700">{num(d.units)}</td>
                  <td className="px-3 py-2.5 text-slate-700">{d.pallets}</td>
                  <td className="px-3 py-2.5">
                    {d.mix
                      ? <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-emerald-500/15 text-emerald-600 border border-emerald-500/30">Ja</span>
                      : <span className="text-slate-400 text-xs">Nej</span>}
                  </td>
                  <td className="px-3 py-2.5 text-slate-700 whitespace-nowrap">{d.deadline}</td>
                  <td className="px-3 py-2.5 text-slate-500 max-w-[220px]">{d.comment}</td>
                  <td className="px-3 py-2.5">
                    {d.image_path ? (
                      <button onClick={() => setPreview({ path: d.image_path, filename: d.image_filename })}
                        className="text-slate-500 hover:text-emerald-600" title={d.image_filename} data-testid={`sg-row-attachment-${d.id}`}>
                        {isPdf(d.image_filename) ? <FileText className="w-4 h-4" /> : <Paperclip className="w-4 h-4" />}
                      </button>
                    ) : <span className="text-slate-300">—</span>}
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-1 justify-end">
                      <Button size="icon" variant="ghost" onClick={() => openEdit(d)} className="h-8 w-8 text-slate-500 hover:text-emerald-600" data-testid={`sg-row-edit-${d.id}`}><Pencil className="w-4 h-4" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => remove(d)} className="h-8 w-8 text-slate-500 hover:text-red-600" data-testid={`sg-row-delete-${d.id}`}><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </td>
                </tr>
              ))}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={9} className="px-3 py-12 text-center text-slate-500" data-testid="sg-empty">Ingen varer endnu. Klik "Ny vare" for at bygge oversigten.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#ffffff] border-[#e2e8f0] text-slate-800 max-w-lg" data-testid="sg-row-dialog">
          <DialogHeader><DialogTitle className="font-heading font-light">{editing ? "Redigér vare" : "Ny vare"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs text-slate-500">Produkt</Label>
              <Input value={form.product} onChange={(e) => setForm((f) => ({ ...f, product: e.target.value }))}
                placeholder="fx frugtkager" className="mt-1 bg-[#f8fafc] border-[#e2e8f0] text-slate-700" data-testid="sg-product-input" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs text-slate-500">Butik</Label>
                <Select value={form.store} onValueChange={(v) => setForm((f) => ({ ...f, store: v }))}>
                  <SelectTrigger className="mt-1 bg-[#f8fafc] border-[#e2e8f0] text-slate-700" data-testid="sg-store-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#ffffff] border-[#e2e8f0] text-slate-800">
                    <SelectItem value="Falster">Falster</SelectItem>
                    <SelectItem value="Ringsted">Ringsted</SelectItem>
                    <SelectItem value="Begge">Begge</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Antal enheder</Label>
                <Input inputMode="decimal" value={form.units} onChange={(e) => setForm((f) => ({ ...f, units: e.target.value }))}
                  placeholder="fx 3240" className="mt-1 bg-[#f8fafc] border-[#e2e8f0] text-slate-700" data-testid="sg-units-input" />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Mix</Label>
                <Select value={form.mix} onValueChange={(v) => setForm((f) => ({ ...f, mix: v }))}>
                  <SelectTrigger className="mt-1 bg-[#f8fafc] border-[#e2e8f0] text-slate-700" data-testid="sg-mix-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#ffffff] border-[#e2e8f0] text-slate-800">
                    <SelectItem value="ja">Ja</SelectItem>
                    <SelectItem value="nej">Nej</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-slate-500">Antal paller</Label>
                <Input value={form.pallets} onChange={(e) => setForm((f) => ({ ...f, pallets: e.target.value }))}
                  placeholder='fx 1,5 eller "23 kolli"' className="mt-1 bg-[#f8fafc] border-[#e2e8f0] text-slate-700" data-testid="sg-pallets-input" />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Aftræk inden</Label>
                <Input value={form.deadline} onChange={(e) => setForm((f) => ({ ...f, deadline: e.target.value }))}
                  placeholder="fx 30-11-2026" className="mt-1 bg-[#f8fafc] border-[#e2e8f0] text-slate-700" data-testid="sg-deadline-input" />
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Kommentarer</Label>
              <textarea value={form.comment} onChange={(e) => setForm((f) => ({ ...f, comment: e.target.value }))}
                rows={2} placeholder="fx mangler blot fordeling og dato"
                className="mt-1 w-full rounded-md bg-[#f8fafc] border border-[#e2e8f0] text-slate-700 text-sm p-2.5 resize-y focus:outline-none focus:ring-1 focus:ring-emerald-400"
                data-testid="sg-comment-input" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Billede/PDF (valgfrit)</Label>
              <input ref={fileRef} type="file" accept="image/*,.pdf,.heic" onChange={handleFile} className="hidden" data-testid="sg-file-input" />
              {form.image_path ? (
                <div className="mt-1 flex items-center gap-3 rounded-lg bg-[#f8fafc] border border-[#e2e8f0] p-2.5">
                  {isPdf(form.image_filename)
                    ? <FileText className="w-9 h-9 text-slate-500 shrink-0" />
                    : <img src={localPreview || imageUrl(form.image_path)} alt="preview" className="w-12 h-12 rounded object-cover shrink-0" />}
                  <span className="flex-1 text-sm text-slate-500 truncate">{form.image_filename}</span>
                  <Button size="icon" variant="ghost" onClick={removeAttachment} className="h-8 w-8 text-slate-500 hover:text-red-600 shrink-0" data-testid="sg-remove-file-btn"><X className="w-4 h-4" /></Button>
                </div>
              ) : (
                <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading}
                  className="mt-1 w-full rounded-lg bg-[#f8fafc] border-[#e2e8f0] border-dashed text-slate-500 hover:bg-[#eef2f6] hover:text-slate-900 gap-2 h-14" data-testid="sg-upload-btn">
                  {uploading ? <><Loader2 className="w-4 h-4 animate-spin" /> Uploader…</> : <><Upload className="w-4 h-4" /> Vedhæft billede eller PDF</>}
                </Button>
              )}
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="ghost" onClick={() => setDialogOpen(false)} className="text-slate-500 hover:text-slate-900" data-testid="sg-cancel-btn">Annullér</Button>
            <Button onClick={save} disabled={saving || uploading} className="rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 gap-2" data-testid="sg-save-btn">
              {saving && <Loader2 className="w-4 h-4 animate-spin" />} {editing ? "Gem" : "Opret"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="bg-[#ffffff] border-[#e2e8f0] text-slate-800 max-w-3xl" data-testid="sg-preview-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading font-light flex items-center gap-2 text-base">
              {preview && isPdf(preview.filename) ? <FileText className="w-4 h-4" /> : <ImageIcon className="w-4 h-4" />}
              {preview?.filename}
            </DialogTitle>
          </DialogHeader>
          {preview && (
            isPdf(preview.filename)
              ? <iframe src={imageUrl(preview.path)} title={preview.filename} className="w-full h-[70vh] rounded-lg bg-white" />
              : <img src={imageUrl(preview.path)} alt={preview.filename} className="w-full max-h-[70vh] object-contain rounded-lg" />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
